import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, 
  Heart, 
  Search, 
  FileText, 
  CheckCircle, 
  CreditCard,
  MessageSquare,
  Star,
  ArrowRight,
  Calendar,
  Shield,
  Clock
} from 'lucide-react';

const HowItWorksPage = () => {
  const navigate = useNavigate();

  const guestSteps = [
    {
      icon: Search,
      title: '1. Hochzeit finden',
      description: 'Durchsuchen Sie unsere vielfältige Auswahl an Hochzeiten weltweit. Nutzen Sie Filter für Ort, Datum, Preis und Kulturkreis, um die perfekte Feier zu finden.',
      details: [
        'Detaillierte Beschreibungen jeder Hochzeit',
        'Fotos und Impressionen',
        'Informationen zu Dresscode und Traditionen',
        'Bewertungen früherer Gäste',
      ],
    },
    {
      icon: FileText,
      title: '2. Bewerbung senden',
      description: 'Erstellen Sie ein aussagekräftiges Profil und bewerben Sie sich bei der Hochzeit Ihrer Wahl. Erzählen Sie dem Brautpaar, warum Sie Teil ihres besonderen Tages sein möchten.',
      details: [
        'Persönliche Nachricht an das Brautpaar',
        'Ihr Profil wird automatisch mitgesendet',
        'Mehrere Bewerbungen gleichzeitig möglich',
        'Status-Updates per E-Mail',
      ],
    },
    {
      icon: CheckCircle,
      title: '3. Genehmigung erhalten',
      description: 'Das Brautpaar prüft Ihre Bewerbung und entscheidet, ob sie Sie einladen möchten. Die meisten Antworten erfolgen innerhalb von 48 Stunden.',
      details: [
        'Benachrichtigung bei Genehmigung',
        'Möglichkeit, Fragen zu stellen',
        'Bei Ablehnung: andere Hochzeiten verfügbar',
      ],
    },
    {
      icon: CreditCard,
      title: '4. Sicher bezahlen',
      description: 'Nach der Genehmigung zahlen Sie den Beitrag sicher über Stripe. Ihr Platz ist erst nach Zahlung reserviert.',
      details: [
        'Alle gängigen Zahlungsmethoden',
        'Sichere Verschlüsselung',
        'Automatische Rechnung',
        'Stornierungsschutz gemäß AGB',
      ],
    },
    {
      icon: MessageSquare,
      title: '5. Mit Gastgebern verbinden',
      description: 'Nach der Buchung können Sie direkt mit dem Brautpaar oder deren Kontaktperson kommunizieren, um Details zu klären.',
      details: [
        'In-App Messaging',
        'Fragen zu Anreise, Unterkunft, Kleidung',
        'Tipps und Empfehlungen',
        'Kennenlernen vor dem Event',
      ],
    },
    {
      icon: Star,
      title: '6. Erleben & Bewerten',
      description: 'Erleben Sie einen unvergesslichen Tag! Nach der Hochzeit können Sie Ihre Erfahrung bewerten und Empfehlungen für andere Reisende hinterlassen.',
      details: [
        'Authentisches Kulturerlebnis',
        'Neue Freundschaften schließen',
        'Bewertung hilft der Community',
        'Fotos teilen (mit Erlaubnis)',
      ],
    },
  ];

  const hostSteps = [
    {
      icon: FileText,
      title: '1. Hochzeit eintragen',
      description: 'Erstellen Sie ein ansprechendes Profil für Ihre Hochzeit. Je mehr Details und Fotos, desto besser können sich potenzielle Gäste ein Bild machen.',
      details: [
        'Beschreibung der Feier und Traditionen',
        'Fotos hochladen',
        'Datum, Ort und Dresscode angeben',
        'Preis pro Gast festlegen',
        'Anzahl der verfügbaren Plätze',
      ],
    },
    {
      icon: Users,
      title: '2. Bewerbungen erhalten',
      description: 'Interessierte Reisende bewerben sich bei Ihnen. Sie sehen deren Profile, Bewerbungsnachricht und können entscheiden, wen Sie einladen möchten.',
      details: [
        'Benachrichtigung bei neuen Bewerbungen',
        'Vollständige Gästeprofile einsehen',
        'Persönliche Nachrichten lesen',
        'Keine Verpflichtung zur Annahme',
      ],
    },
    {
      icon: CheckCircle,
      title: '3. Gäste auswählen',
      description: 'Wählen Sie die Gäste aus, die zu Ihrer Feier passen. Sie haben die volle Kontrolle darüber, wen Sie einladen.',
      details: [
        'Genehmigen oder ablehnen mit einem Klick',
        'Nachricht an Bewerber senden',
        'Anzahl der Gäste selbst bestimmen',
      ],
    },
    {
      icon: CreditCard,
      title: '4. Zahlung erhalten',
      description: 'Nach Genehmigung zahlen die Gäste sicher über Stripe. Die Auszahlung an Sie erfolgt automatisch 48 Stunden nach dem Event.',
      details: [
        'Sie erhalten 85% des Betrags',
        'Automatische Auszahlung',
        'Übersichtliches Dashboard',
        'Alle Transaktionen einsehbar',
      ],
    },
    {
      icon: MessageSquare,
      title: '5. Gäste kennenlernen',
      description: 'Nutzen Sie das Messaging-System, um Ihre Gäste vor der Hochzeit kennenzulernen und wichtige Informationen auszutauschen.',
      details: [
        'Anreise-Tipps geben',
        'Unterkunftsempfehlungen',
        'Traditionelle Kleidung organisieren',
        'Fragen beantworten',
      ],
    },
    {
      icon: Heart,
      title: '6. Gemeinsam feiern',
      description: 'Teilen Sie Ihren besonderen Tag mit Menschen aus aller Welt und schaffen Sie unvergessliche interkulturelle Momente.',
      details: [
        'Einzigartige Hochzeitserinnerungen',
        'Neue internationale Freundschaften',
        'Positive Bewertungen sammeln',
        'Teil der WEDVAYA-Community werden',
      ],
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen" data-testid="how-it-works-page">
        {/* Hero */}
        <section className="bg-primary py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
              So funktioniert WEDVAYA
            </h1>
            <p className="text-white/90 text-lg max-w-2xl mx-auto">
              In wenigen einfachen Schritten zu Ihrem unvergesslichen Hochzeitserlebnis – 
              ob als Gast oder als Gastgeber.
            </p>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-12 bg-white border-b">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">100% Sicher</h3>
                <p className="text-sm text-muted-foreground">
                  Sichere Zahlungen über Stripe. Geld-zurück-Garantie bei Absage.
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Schnelle Antworten</h3>
                <p className="text-sm text-muted-foreground">
                  Die meisten Brautpaare antworten innerhalb von 48 Stunden.
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Flexible Planung</h3>
                <p className="text-sm text-muted-foreground">
                  Kostenlose Stornierung bis 30 Tage vor dem Event.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="py-20 bg-secondary">
          <div className="max-w-6xl mx-auto px-4">
            <Tabs defaultValue="guest" className="w-full">
              <div className="flex justify-center mb-12">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                  <TabsTrigger value="guest" className="py-3">
                    <Users className="h-4 w-4 mr-2" />
                    Für Gäste
                  </TabsTrigger>
                  <TabsTrigger value="host" className="py-3">
                    <Heart className="h-4 w-4 mr-2" />
                    Für Gastgeber
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="guest">
                <div className="space-y-8">
                  {guestSteps.map((step, index) => (
                    <Card key={index} className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="grid grid-cols-1 lg:grid-cols-3">
                          <div className="bg-primary/5 p-8 flex flex-col justify-center">
                            <div className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center mb-4">
                              <step.icon className="h-7 w-7" />
                            </div>
                            <h3 className="font-serif text-2xl font-bold mb-2">{step.title}</h3>
                            <p className="text-muted-foreground">{step.description}</p>
                          </div>
                          <div className="lg:col-span-2 p-8">
                            <h4 className="font-semibold mb-4">Was Sie erwartet:</h4>
                            <ul className="space-y-3">
                              {step.details.map((detail, i) => (
                                <li key={i} className="flex items-center gap-3">
                                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                  <span className="text-muted-foreground">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="text-center mt-12">
                  <Button size="lg" className="rounded-full px-8" onClick={() => navigate('/weddings')}>
                    Hochzeiten entdecken
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="host">
                <div className="space-y-8">
                  {hostSteps.map((step, index) => (
                    <Card key={index} className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="grid grid-cols-1 lg:grid-cols-3">
                          <div className="bg-primary/5 p-8 flex flex-col justify-center">
                            <div className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center mb-4">
                              <step.icon className="h-7 w-7" />
                            </div>
                            <h3 className="font-serif text-2xl font-bold mb-2">{step.title}</h3>
                            <p className="text-muted-foreground">{step.description}</p>
                          </div>
                          <div className="lg:col-span-2 p-8">
                            <h4 className="font-semibold mb-4">Was Sie erwartet:</h4>
                            <ul className="space-y-3">
                              {step.details.map((detail, i) => (
                                <li key={i} className="flex items-center gap-3">
                                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                  <span className="text-muted-foreground">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="text-center mt-12">
                  <Button size="lg" className="rounded-full px-8" onClick={() => navigate('/register?role=host')}>
                    Gastgeber werden
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* FAQ Preview */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-serif text-3xl font-bold mb-4">Noch Fragen?</h2>
            <p className="text-muted-foreground mb-8">
              Finden Sie Antworten in unseren häufig gestellten Fragen.
            </p>
            <Button variant="outline" size="lg" className="rounded-full" asChild>
              <Link to="/faq">
                Zu den FAQ
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HowItWorksPage;
